'use client';

import { useEffect, useState } from 'react';
import { format, parseISO, startOfDay } from 'date-fns';

type Calendar = {
    schedule: 'recurring' | 'single_date'; event_date: string | null;
    periods: Array<{ id: number; start: string | null; end: string | null }>;
    days: Array<{ date: string; is_bookable: boolean; remaining_pax: number | null }>;
};

export function useDailyTourCalendar(enabled: boolean, productId: number, selectedDate: Date | undefined, onSelect: (date: Date) => void) {
    const [month, setMonth] = useState(() => startOfDay(new Date()));
    const [calendar, setCalendar] = useState<Calendar | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);
    const monthKey = format(month, 'yyyy-MM');
    useEffect(() => {
        if (enabled && selectedDate) setMonth(startOfDay(selectedDate));
    }, [enabled, selectedDate]);
    useEffect(() => {
        if (!enabled) return;
        const abort = new AbortController();
        setLoading(true);
        setError(false);
        fetch(`/api/daily-tours/calendar?product_id=${productId}&month=${monthKey}`, { signal: abort.signal, cache: 'no-store' })
            .then(async (response) => {
                const payload = await response.json();
                if (!response.ok || !payload.data) throw new Error('Calendar unavailable');
                const data = payload.data as Calendar;
                setCalendar(data);
                const first = data.schedule === 'single_date' ? data.event_date : data.periods.map((p) => p.start).filter((d): d is string => Boolean(d)).sort()[0];
                if (!selectedDate && first && first >= format(new Date(), 'yyyy-MM-dd') && format(month, 'yyyy-MM') < first.slice(0, 7)) {
                    setMonth(parseISO(first));
                }
                if (data.schedule === 'single_date' && data.event_date && (!selectedDate || format(selectedDate, 'yyyy-MM-dd') !== data.event_date)) {
                    setMonth(parseISO(data.event_date));
                    onSelect(parseISO(data.event_date));
                }
            })
            .catch(() => { if (!abort.signal.aborted) { setCalendar(null); setError(true); } })
            .finally(() => { if (!abort.signal.aborted) setLoading(false); });
        return () => abort.abort();
    }, [enabled, productId, monthKey, selectedDate, onSelect]);
    return {
        calendar, loading, error, month, onMonthChange: setMonth,
        disabled: (date: Date) => {
            const key = format(date, 'yyyy-MM-dd');
            return date < startOfDay(new Date()) || loading || !calendar?.days.find((day) => day.date === key)?.is_bookable;
        },
    };
}
